import React from 'react';
import styled from 'styled-components';
import {
  AutocompleteGroupedOption,
  AutocompleteInputProps,
  AutocompleteValue,
  UseAutocompleteProps,
  useAutocomplete,
} from '../hooks/use-autocomplete';
import { useForcedRerender } from '../hooks/use-forced-rerender';
import { useIsomorphicLayoutEffect } from '../hooks/useIsomorphicLayoutEffect';
import { Chip } from './Chip';
import { Popper, PopperOffset, PopperPlacement } from './Popper';

declare module '@nucleus/react-components' {
  interface ReactComponentsTheme {
    Autocomplete?: {
      panel?: {
        background?: string;
        color?: string;
        boxShadow?: string;
        borderRadius?: string;
      };
      option?: {
        hoverBackground?: string;
        focusedBackground?: string;
        selectedBackground?: string;
      };
      noOptionsColor?: string;
      loadingColor?: string;
      groupLabelColor?: string;
      groupLabelBackground?: string;
    };
  }
}

export interface AutocompleteRenderInputParams {
  id: string;
  disabled: boolean;
  fullWidth: boolean;
  size: 'small' | undefined;
  inputProps: AutocompleteInputProps;
  inputLabelProps: ReturnType<ReturnType<typeof useAutocomplete>['getInputLabelProps']>;
  /**
   * The ref MUST be attached to the same element you want to anchor the
   * Popper to (typically the wrapper around the actual <input>).
   */
  inputRef: React.Ref<HTMLDivElement>;
  /**
   * Pre-built end adornment containing the optional clear and popup buttons,
   * or `null` when neither should be shown. Render this inside your input.
   *
   * Ignore this and render your own button using `popupOpen`,
   * `getClearProps`, and `getPopupIndicatorProps` if you need a custom layout
   * (for example, a single toggle that swaps icons on open/close).
   */
  endAdornment: React.ReactNode;
  /**
   * Pre-built start adornment containing chips for `multiple` mode, or
   * `null` when no chips should be shown.
   */
  startAdornment: React.ReactNode;
  /**
   * Whether the popup listbox is currently open.
   */
  popupOpen: boolean;
  /**
   * Returns props to spread on a custom clear button. Use this when building
   * your own end-adornment instead of relying on the pre-built `endAdornment`.
   */
  getClearProps: () => React.ButtonHTMLAttributes<HTMLButtonElement>;
  /**
   * Returns props to spread on a custom popup-toggle button. Use this when
   * building your own end-adornment instead of relying on the pre-built
   * `endAdornment`.
   */
  getPopupIndicatorProps: () => React.ButtonHTMLAttributes<HTMLButtonElement>;
}

export interface AutocompleteRenderOptionState {
  selected: boolean;
  index: number;
  inputValue: string;
}

export interface AutocompleteRenderGroupParams {
  key: number | string;
  group: string;
  children?: React.ReactNode;
}

export interface AutocompleteRenderValueProps {
  index: number;
  onDelete: (event: React.SyntheticEvent) => void;
  className: string;
  'data-item-index': number;
  tabIndex: number;
}

export type AutocompleteSize = 'small' | 'medium';

export interface AutocompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends UseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> {
  /**
   * Class name applied to the popup panel (the styled card that wraps the
   * listbox). Mirrors `Menu.tsx`, where `className` flows to the inner panel
   * rather than the outer trigger wrapper. Style the input itself inside your
   * `renderInput` callback.
   */
  className?: string;
  /**
   * Inline style applied to the popup panel.
   */
  style?: React.CSSProperties;
  /**
   * Icon shown inside the clear button. Defaults to a simple "x" character.
   */
  clearIcon?: React.ReactNode;
  /**
   * Accessible label for the clear button.
   * @default 'Clear'
   */
  clearText?: string;
  /**
   * Accessible label for the popup-toggle button when the popup is open.
   * @default 'Close'
   */
  closeText?: string;
  /**
   * Accessible label for the popup-toggle button when the popup is closed.
   * @default 'Open'
   */
  openText?: string;
  /**
   * Force the popup-icon visibility.
   * - `true` to always show the popup icon.
   * - `false` to never show it.
   * - `'auto'` (default) to show it unless `freeSolo` is true.
   * @default 'auto'
   */
  forcePopupIcon?: boolean | 'auto';
  /**
   * If true, the input takes up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Function returning the label shown when tags are truncated by `limitTags`.
   * @default (more) => `+${more}`
   */
  getLimitTagsText?: (more: number) => React.ReactNode;
  /**
   * Maximum number of tags shown when `multiple` is enabled and the input is
   * not focused. Set to `-1` to disable truncation.
   * @default -1
   */
  limitTags?: number;
  /**
   * If true, the listbox shows a loading state instead of options.
   * @default false
   */
  loading?: boolean;
  /**
   * Text shown while `loading` is true and there are no options to render.
   * @default 'Loading…'
   */
  loadingText?: React.ReactNode;
  /**
   * Text shown when the listbox is open but no options match.
   * @default 'No options'
   */
  noOptionsText?: React.ReactNode;
  /**
   * Icon shown inside the popup-toggle button. Defaults to a chevron-down glyph.
   */
  popupIcon?: React.ReactNode;
  /**
   * If true, the listbox is rendered in place rather than through a Portal.
   * @default false
   */
  disablePortal?: boolean;
  /**
   * Where the popup panel should be positioned relative to the input.
   * Hoisted from `<Popper>` for ergonomic access.
   * @default 'bottom-start'
   */
  popperPlacement?: PopperPlacement;
  /**
   * Pixel offset applied to the popup panel after positioning. Useful for
   * adding spacing between the input and the popup.
   */
  popperOffset?: PopperOffset;
  /**
   * The component used for the popup panel that wraps the listbox.
   * Receives `className`, `style`, and `children` as plain props. Mirrors
   * Menu's `menuListComponent` swap pattern.
   * @default Panel
   */
  panelComponent?: React.JSXElementConstructor<
    React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }
  >;
  /**
   * The component used for the listbox `<ul>` inside the panel.
   * Receives the props returned by `getListboxProps()` (including ref).
   * @default Listbox
   */
  listboxComponent?: React.JSXElementConstructor<
    React.HTMLAttributes<HTMLUListElement> & { children?: React.ReactNode }
  >;
  /**
   * Size of the component, passed through to `renderInput`.
   * @default 'medium'
   */
  size?: AutocompleteSize;
  /**
   * Renders the option element inside the listbox.
   *
   * Defaults to a plain `<li>` containing the option's label.
   */
  renderOption?: (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: Value,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  /**
   * Required. Renders the input element. Spread `inputProps` on the
   * underlying `<input>` and attach `inputRef` to the wrapper element you
   * want the listbox to anchor to.
   */
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
  /**
   * Renders a group header in the listbox (used together with `groupBy`).
   */
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  /**
   * Renders the selected value(s) inside the input as chips/tags.
   * When supplied for the single-select case, single-value rendering becomes
   * tag-like (Backspace removes the value instead of clearing the input).
   */
  renderValue?: (
    value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
    getItemProps: (params: { index: number }) => AutocompleteRenderValueProps,
    ownerState: AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo>
  ) => React.ReactNode;
}

export interface AutocompleteOwnerState<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> {
  disablePortal: boolean;
  expanded: boolean;
  focused: boolean;
  fullWidth: boolean;
  hasClearIcon: boolean;
  hasPopupIcon: boolean;
  inputFocused: boolean;
  popupOpen: boolean;
  size: AutocompleteSize;
  multiple: boolean;
  freeSolo: boolean;
  loading: boolean;
  value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
}

const DefaultClearIcon = (
  <span aria-hidden="true" style={{ fontSize: '18px', lineHeight: 1 }}>
    ×
  </span>
);

const DefaultPopupIcon = (
  <span aria-hidden="true" style={{ fontSize: '12px', lineHeight: 1 }}>
    ▾
  </span>
);

/**
 * The Autocomplete component combines an input with a popup listbox of options.
 *
 * It supports single and multi-select, free-solo input, async loading, grouping,
 * custom filtering, and full keyboard navigation. The headless logic lives in
 * `useAutocomplete`; this component composes the visual pieces (input, clear/popup
 * indicators, Popper, Paper, Listbox, Chip).
 *
 * Inspired by [material-ui/autocomplete](https://mui.com/material-ui/api/autocomplete/).
 *
 * See `createFilterOptions` for customizing the built-in filter behavior.
 *
 * @param props - The props for the Autocomplete component.
 * @returns A JSX element representing the Autocomplete.
 */
export const Autocomplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>
): JSX.Element => {
  const {
    className,
    style,
    clearIcon = DefaultClearIcon,
    clearText = 'Clear',
    closeText = 'Close',
    openText = 'Open',
    forcePopupIcon = 'auto',
    fullWidth = false,
    getLimitTagsText = (more: number) => `+${more}`,
    limitTags = -1,
    loading = false,
    loadingText = 'Loading\u2026',
    noOptionsText = 'No options',
    popupIcon = DefaultPopupIcon,
    disablePortal = false,
    popperPlacement = 'bottom-start' as PopperPlacement,
    popperOffset,
    panelComponent: PanelComponent = Panel,
    listboxComponent: ListboxComponent = Listbox,
    size = 'medium' as AutocompleteSize,
    renderInput,
    renderOption,
    renderGroup,
    renderValue,
    multiple,
    freeSolo,
    disabled = false,
    readOnly = false,
    disableClearable,
    ...other
  } = props;

  const autocompleteState = useAutocomplete<Value, Multiple, DisableClearable, FreeSolo>({
    ...other,
    multiple: multiple,
    freeSolo: freeSolo,
    disabled: disabled,
    readOnly: readOnly,
    disableClearable: disableClearable,
    renderValue: renderValue,
    componentName: 'Autocomplete',
  });

  const {
    getRootProps,
    getInputProps,
    getInputLabelProps,
    getPopupIndicatorProps,
    getClearProps,
    getItemProps,
    getListboxProps,
    getOptionProps,
    value,
    dirty,
    expanded,
    id,
    popupOpen,
    focused,
    focusedItem,
    anchorEl,
    setAnchorEl,
    inputValue,
    groupedOptions,
  } = autocompleteState;

  const forceRender = useForcedRerender();

  React.useEffect(() => {
    if (!popupOpen || !anchorEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let lastWidth = anchorEl.clientWidth;
    const observer = new ResizeObserver(() => {
      const newWidth = anchorEl.clientWidth;
      if (lastWidth !== newWidth) {
        lastWidth = newWidth;
        forceRender();
      }
    });
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [popupOpen, anchorEl, forceRender]);

  const previousGroupedOptionsRef = React.useRef<typeof groupedOptions>([] as unknown as typeof groupedOptions);
  const previousPopupOpenRef = React.useRef<boolean>(false);
  const renderedOptions = popupOpen ? groupedOptions : previousGroupedOptionsRef.current;

  useIsomorphicLayoutEffect(() => {
    if (popupOpen && !previousPopupOpenRef.current) {
      previousGroupedOptionsRef.current = [] as unknown as typeof groupedOptions;
    }
    previousPopupOpenRef.current = popupOpen;
    if (popupOpen && groupedOptions.length > 0) {
      previousGroupedOptionsRef.current = groupedOptions;
    }
  }, [popupOpen, groupedOptions]);

  const hasClearIcon = !disableClearable && !disabled && dirty && !readOnly;
  const hasPopupIcon = (!freeSolo || forcePopupIcon === true) && forcePopupIcon !== false;

  const ownerState: AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo> = {
    disablePortal: disablePortal,
    expanded: expanded,
    focused: focused,
    fullWidth: fullWidth,
    hasClearIcon: hasClearIcon,
    hasPopupIcon: hasPopupIcon,
    inputFocused: focusedItem === -1,
    popupOpen: popupOpen,
    size: size,
    multiple: Boolean(multiple),
    freeSolo: Boolean(freeSolo),
    loading: loading,
    value: value,
  };

  const defaultGetOptionLabel = (option: Value): string => {
    const label = (option as { label?: unknown })?.label;
    if (typeof label === 'string') {
      return label;
    }
    return String(option);
  };

  const getOptionLabel = (other.getOptionLabel as ((option: Value) => string) | undefined) ?? defaultGetOptionLabel;

  const buildCustomItemProps = (params: { index: number }): AutocompleteRenderValueProps => {
    const itemProps = (getItemProps as unknown as (args: { index: number }) => Record<string, unknown>)(
      params
    );
    return {
      index: params.index,
      onDelete: (itemProps.onDelete as (event: React.SyntheticEvent) => void) ?? (() => undefined),
      className: 'NucleusAutocomplete-tag',
      'data-item-index': itemProps['data-item-index'] as number,
      tabIndex: itemProps.tabIndex as number,
    };
  };

  let startAdornment: React.ReactNode = null;
  const valueAsArray: Array<Value> = multiple
    ? (value as unknown as Array<Value>)
    : value != null
      ? [value as unknown as Value]
      : [];

  if (multiple) {
    if (valueAsArray.length > 0) {
      if (renderValue) {
        startAdornment = renderValue(value, buildCustomItemProps, ownerState);
      } else {
        const tags = valueAsArray.map((option, index) => {
          const customItemProps = buildCustomItemProps({ index: index });
          return (
            <Chip
              key={index}
              data-item-index={customItemProps['data-item-index']}
              tabIndex={customItemProps.tabIndex}
              className={customItemProps.className}
              label={getOptionLabel(option)}
              size={size}
              disabled={disabled}
              onDelete={readOnly ? undefined : customItemProps.onDelete}
            />
          );
        });

        if (limitTags > -1 && Array.isArray(tags)) {
          const more = tags.length - limitTags;
          if (!focused && more > 0) {
            const visible = tags.slice(0, limitTags);
            visible.push(
              <span className="NucleusAutocomplete-tag" key={`limit-${limitTags}`}>
                {getLimitTagsText(more)}
              </span>
            );
            startAdornment = visible;
          } else {
            startAdornment = tags;
          }
        } else {
          startAdornment = tags;
        }
      }
    }
  } else if (renderValue && value != null) {
    startAdornment = renderValue(value, buildCustomItemProps, ownerState);
  }

  const endAdornment =
    hasClearIcon || hasPopupIcon ? (
      <EndAdornment>
        {hasClearIcon ? (
          <IndicatorButton
            {...getClearProps()}
            aria-label={clearText}
            title={clearText}
          >
            {clearIcon}
          </IndicatorButton>
        ) : null}
        {hasPopupIcon ? (
          <IndicatorButton
            {...getPopupIndicatorProps()}
            disabled={disabled}
            aria-label={popupOpen ? closeText : openText}
            title={popupOpen ? closeText : openText}
            data-popup-open={popupOpen || undefined}
          >
            {popupIcon}
          </IndicatorButton>
        ) : null}
      </EndAdornment>
    ) : null;

  const inputProps = getInputProps();
  const inputLabelProps = getInputLabelProps();

  const renderInputParams: AutocompleteRenderInputParams = {
    id: id,
    disabled: disabled,
    fullWidth: fullWidth,
    size: size === 'small' ? 'small' : undefined,
    inputProps: inputProps,
    inputLabelProps: inputLabelProps,
    inputRef: setAnchorEl as React.Ref<HTMLDivElement>,
    endAdornment: endAdornment,
    startAdornment: startAdornment,
    popupOpen: popupOpen,
    getClearProps: getClearProps,
    getPopupIndicatorProps: getPopupIndicatorProps,
  };

  const defaultRenderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: Value
  ): React.ReactNode => {
    const { key, ...rest } = optionProps;
    return (
      <li key={key} {...rest}>
        {getOptionLabel(option)}
      </li>
    );
  };

  const defaultRenderGroup = (params: AutocompleteRenderGroupParams): React.ReactNode => (
    <li key={params.key}>
      <GroupLabel>{params.group}</GroupLabel>
      <GroupUl>{params.children}</GroupUl>
    </li>
  );

  const renderListOption = (option: Value, index: number) => {
    const optionProps = getOptionProps({ option: option, index: index });
    return (renderOption ?? defaultRenderOption)(
      { ...optionProps, className: 'NucleusAutocomplete-option' },
      option,
      {
        selected: optionProps['aria-selected'] === true,
        index: index,
        inputValue: inputValue,
      }
    );
  };

  const isGrouped = (
    items: typeof renderedOptions
  ): items is Array<AutocompleteGroupedOption<Value>> => {
    return Array.isArray(items) && items.length > 0 && (items[0] as AutocompleteGroupedOption<Value>).group !== undefined;
  };

  const renderGroupItem = renderGroup ?? defaultRenderGroup;

  const listboxProps = getListboxProps();

  const hasPopupContent = renderedOptions.length > 0 || loading || !freeSolo;
  const shouldRenderPopper = anchorEl != null && hasPopupContent;

  const rootProps = getRootProps();

  return (
    <>
      <Root
        {...rootProps}
        className="NucleusAutocomplete"
        data-fullwidth={fullWidth || undefined}
        data-focused={focused || undefined}
        data-expanded={expanded || undefined}
      >
        {renderInput(renderInputParams)}
      </Root>
      {shouldRenderPopper ? (
        <Popper
          anchorEl={anchorEl}
          isOpen={popupOpen}
          placement={popperPlacement}
          offset={popperOffset}
          matchAnchorWidth
          disablePortal={disablePortal}
        >
          <PanelComponent className={className} style={style}>
            {loading && renderedOptions.length === 0 ? (
              <Loading>{loadingText}</Loading>
            ) : null}
            {renderedOptions.length === 0 && !freeSolo && !loading ? (
              <NoOptions
                role="presentation"
                onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
              >
                {noOptionsText}
              </NoOptions>
            ) : null}
            {renderedOptions.length > 0 ? (
              <ListboxComponent {...listboxProps}>
                {isGrouped(renderedOptions)
                  ? renderedOptions.map((groupedOption) =>
                      renderGroupItem({
                        key: groupedOption.key,
                        group: groupedOption.group,
                        children: groupedOption.options.map((option, optionIndex) =>
                          renderListOption(option, groupedOption.index + optionIndex)
                        ),
                      })
                    )
                  : (renderedOptions as Array<Value>).map((option, index) =>
                      renderListOption(option, index)
                    )}
              </ListboxComponent>
            ) : null}
          </PanelComponent>
        </Popper>
      ) : null}
    </>
  );
};

Autocomplete.defaultProps = {
  autoComplete: false,
  autoHighlight: false,
  autoSelect: false,
  blurOnSelect: false,
  clearOnEscape: false,
  disableClearable: false,
  disableCloseOnSelect: false,
  disabled: false,
  disabledItemsFocusable: false,
  disableListWrap: false,
  disablePortal: false,
  filterSelectedOptions: false,
  forcePopupIcon: 'auto' as const,
  freeSolo: false,
  fullWidth: false,
  includeInputInList: false,
  limitTags: -1,
  loading: false,
  loadingText: 'Loading\u2026',
  multiple: false,
  noOptionsText: 'No options',
  openOnFocus: false,
  readOnly: false,
  size: 'medium' as AutocompleteSize,
  clearText: 'Clear',
  closeText: 'Close',
  openText: 'Open',
};

const Root = styled.div`
  position: relative;

  &[data-fullwidth] {
    width: 100%;
  }
`;

const Panel = styled.div`
  background: ${({ theme }) => theme._reactComponents.Autocomplete?.panel?.background ?? '#ffffff'};
  color: ${({ theme }) => theme._reactComponents.Autocomplete?.panel?.color ?? 'inherit'};
  box-shadow: ${({ theme }) =>
    theme._reactComponents.Autocomplete?.panel?.boxShadow ?? '0 4px 16px rgba(0, 0, 0, 0.12)'};
  border-radius: ${({ theme }) => theme._reactComponents.Autocomplete?.panel?.borderRadius ?? '8px'};
  overflow: hidden;
`;

const Listbox = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 0;
  max-height: 40vh;
  overflow: auto;
  position: relative;

  & .NucleusAutocomplete-option {
    min-height: 40px;
    display: flex;
    overflow: hidden;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    padding: 6px 16px;
    box-sizing: border-box;
    outline: 0;
    -webkit-tap-highlight-color: transparent;

    &.nucleus-focused {
      background: ${({ theme }) =>
        theme._reactComponents.Autocomplete?.option?.focusedBackground ?? 'rgba(0, 0, 0, 0.04)'};
    }

    &[aria-disabled='true'] {
      opacity: 0.4;
      pointer-events: none;
    }

    &[aria-selected='true'] {
      background: ${({ theme }) =>
        theme._reactComponents.Autocomplete?.option?.selectedBackground ?? 'rgba(157, 61, 242, 0.08)'};
    }

    @media (hover: hover) {
      &:hover {
        background: ${({ theme }) =>
          theme._reactComponents.Autocomplete?.option?.hoverBackground ?? 'rgba(0, 0, 0, 0.04)'};
      }
    }
  }
`;

const NoOptions = styled.div`
  color: ${({ theme }) => theme._reactComponents.Autocomplete?.noOptionsColor ?? 'rgba(0, 0, 0, 0.6)'};
  padding: 14px 16px;
`;

const Loading = styled.div`
  color: ${({ theme }) => theme._reactComponents.Autocomplete?.loadingColor ?? 'rgba(0, 0, 0, 0.6)'};
  padding: 14px 16px;
`;

const GroupLabel = styled.div`
  background: ${({ theme }) => theme._reactComponents.Autocomplete?.groupLabelBackground ?? 'transparent'};
  color: ${({ theme }) => theme._reactComponents.Autocomplete?.groupLabelColor ?? 'rgba(0, 0, 0, 0.6)'};
  padding: 8px 16px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.04em;
  position: sticky;
  top: -8px;
`;

const GroupUl = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  & .NucleusAutocomplete-option {
    padding-left: 24px;
  }
`;

const EndAdornment = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const IndicatorButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 4px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
  border-radius: 4px;
  transition: transform 150ms ease-out;

  &[data-popup-open] {
    transform: rotate(180deg);
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: rgba(0, 0, 0, 0.04);
    }
  }
`;

export { createFilterOptions } from '../utils/create-filter-options';
export type { CreateFilterOptionsConfig, FilterOptionsState } from '../utils/create-filter-options';
